import {
  downloadExportFile,
  fetchNotifications,
  markNotificationRead
} from '@/services/notifications-api.service'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import download from 'downloadjs'
import { computed, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import dayjs from '@/lib/dayjs'
import socketService from '@/services/socket.service'
import { openComment } from '@/hooks/use-open-comment'
import useToast from '@/hooks/use-toast'
import { logger } from '@/utils/logger'
import {
  NOTIFICATION_ALL_INVALIDATION_KEY,
  NOTIFICATION_FILTER,
  NOTIFICATION_TYPE,
  NOTIFICATION_UNREAD_INVALIDATION_KEY,
  NOTIFICATION_TIME_ON_SCREEN,
  NOTIFICATIONS_PAGE_SIZE,
  NOTIFICATIONS_UPDATED_EVENT,
  UI_BEHAVIOUR
} from '@/lib/NotificationConstants'

const EMPTY_PAGE = Object.freeze({ notifications: [], totalCount: 0, nextOffset: null })

const EMPTY_DATA = Object.freeze({ pages: [EMPTY_PAGE], pageParams: [0] })

const DATE_FORMAT = 'MMM D'

const FILE_EXPORT_TYPES = new Set([
  NOTIFICATION_TYPE.CSV_FILE_PEOPLE_EXPORT,
  NOTIFICATION_TYPE.PPT_FILE_ORG_CHART_EXPORT
])

/**
 * Downloads a file-export notification's file, showing an error toast when the server refuses it.
 * @param {object} notification Notification carrying `boardId`, `runArgs.docId`, and `runArgs.fileName`.
 * @param {Function} queueErrorToast Shows an error toast.
 * @returns {Promise<void>} Resolves once the file is saved or the error is shown.
 */
async function downloadExport(notification, queueErrorToast) {
  try {
    const file = await downloadExportFile(notification.boardId, notification.runArgs.docId)
    download(file, notification.runArgs.fileName)
  } catch (error) {
    logger.error('Failed to download export file:', error.message)
    queueErrorToast('This file could not be downloaded. It may have expired.')
  }
}

/**
 * Ids the comment hook needs. Navigation stays in that hook.
 * Comment ids come from `runArgs`.
 * @param {object} notification Clicked notification.
 * @returns {{boardId: string, planBoardId: string, commentId: string|null, messageId: string|null, positionId: string|null}} Open target.
 */
function openTargetComment(notification) {
  const runArgs = notification.runArgs || {}
  const messageId = runArgs.messageId || runArgs.commentId || null
  return {
    boardId: notification.boardId,
    planBoardId: notification.planBoardId,
    commentId: runArgs.commentId || messageId,
    messageId,
    positionId: runArgs.positionId || null
  }
}

/**
 * Runs the click action for a notification and marks it read.
 * @param {object} notification Notification to act on.
 * @param {Function} markRead Marks a notification id read.
 * @param {Function} queueErrorToast Shows an error toast.
 * @returns {boolean} False when the notification has no live action.
 */
function handleNotificationAction(notification, markRead, queueErrorToast) {
  if (!notification.readAt) {
    markRead(notification.id)
  }

  if (notification.notificationType === NOTIFICATION_TYPE.COMMENT) {
    return openComment(openTargetComment(notification))
  }

  if (!notification.uiBehaviours?.includes(UI_BEHAVIOUR.ACTIONABLE)) {
    return false
  }

  if (FILE_EXPORT_TYPES.has(notification.notificationType)) {
    return downloadExport(notification, queueErrorToast)
  }

  return false
}

/**
 * Provides actions shared by notification lists and notification toasts.
 * @returns {{runAction: Function}} Notification actions.
 */
export function useNotificationActions() {
  const queryClient = useQueryClient()
  const { mutate: markRead } = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_ALL_INVALIDATION_KEY }),
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_INVALIDATION_KEY })
      ])
  })
  const { queueErrorToast } = useToast()

  return {
    runAction: (notification) => handleNotificationAction(notification, markRead, queueErrorToast)
  }
}

/**
 * Phrases how long ago a notification arrived today.
 * @param {object} createdAt Dayjs instance for when the notification arrived.
 * @param {object} now Dayjs instance for the current time.
 * @returns {string} Display time.
 */
function timeAgo(createdAt, now) {
  const minutes = now.diff(createdAt, 'minute')

  if (minutes < 1) {
    return 'just now'
  }

  if (minutes === 1) {
    return '1 min ago'
  }

  if (minutes < 60) {
    return `${minutes} mins ago`
  }

  const hours = now.diff(createdAt, 'hour')

  if (hours === 1) {
    return '1 hour ago'
  }

  return `${hours} hours ago`
}

/**
 * Splits notifications into today and older, labelling each with its display time.
 * Reads `updatedAt`, the field the server pages by, so groups follow the order they arrive in.
 * @param {Array<object>} notifications Notifications in server order.
 * @param {Function} runAction Runs a notification's click action.
 * @returns {{today: Array<object>, older: Array<object>}} Notifications ready to render.
 */
function groupNotifications(notifications, runAction) {
  const now = dayjs()
  const startOfToday = now.startOf('day')
  const today = []
  const older = []

  for (const notification of notifications) {
    const updatedAt = dayjs(notification.updatedAt)
    const item = {
      ...notification,
      unread: !notification.readAt,
      onClick: () => runAction(notification)
    }

    if (updatedAt.isBefore(startOfToday)) {
      item.time = updatedAt.format(DATE_FORMAT)
      older.push(item)
    } else {
      item.time = timeAgo(updatedAt, now)
      today.push(item)
    }
  }

  return { today, older }
}

/**
 * Notifications belong to a main board and live in that board's database, so there is nothing to
 * fetch until the user has one.
 * @param {object} getters - Vuex getters.
 * @returns {boolean} True when the user has at least one main board.
 */
function hasMainBoard(getters) {
  return Boolean(getters.mainBoards?.length)
}

/**
 * Fetches the user's complete notification list.
 * @returns {object} Groups, count, loading state, and pagination for all notifications.
 */
export function useAllNotifications() {
  const { runAction } = useNotificationActions()
  const { getters } = useStore()
  const enabled = computed(() => hasMainBoard(getters))

  const query = useInfiniteQuery({
    queryKey: NOTIFICATION_ALL_INVALIDATION_KEY,
    queryFn: ({ pageParam }) =>
      fetchNotifications({
        filter: NOTIFICATION_FILTER.all,
        limit: NOTIFICATIONS_PAGE_SIZE,
        offset: pageParam
      }),
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    placeholderData: EMPTY_DATA,
    staleTime: Infinity
  })

  const groups = computed(() =>
    groupNotifications(
      query.data.value.pages.flatMap((page) => page.notifications),
      runAction
    )
  )

  const totalCount = computed(() => {
    const [newestPage] = query.data.value.pages

    return newestPage.totalCount
  })

  return {
    groups,
    totalCount,
    isLoading: computed(() => enabled.value && query.isPlaceholderData.value),
    isLoadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage
  }
}

/**
 * Fetches the user's unread notification list.
 * @returns {object} Groups, count, loading state, and pagination for unread notifications.
 */
export function useUnreadNotifications() {
  const { runAction } = useNotificationActions()
  const { getters } = useStore()
  const enabled = computed(() => hasMainBoard(getters))

  const query = useInfiniteQuery({
    queryKey: NOTIFICATION_UNREAD_INVALIDATION_KEY,
    queryFn: ({ pageParam }) =>
      fetchNotifications({
        filter: NOTIFICATION_FILTER.unread,
        limit: NOTIFICATIONS_PAGE_SIZE,
        offset: pageParam
      }),
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    placeholderData: EMPTY_DATA,
    staleTime: Infinity
  })

  const groups = computed(() =>
    groupNotifications(
      query.data.value.pages.flatMap((page) => page.notifications),
      runAction
    )
  )

  const totalCount = computed(() => {
    const [newestPage] = query.data.value.pages

    return newestPage.totalCount
  })

  return {
    groups,
    totalCount,
    isLoading: computed(() => enabled.value && query.isPlaceholderData.value),
    isLoadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage
  }
}

/**
 * Connects the signed-in user to notification updates.
 */
export function useConnectSocket({ autoConnect = true } = {}) {
  const { getters } = useStore()
  const queryClient = useQueryClient()
  const { queueNotificationToast } = useToast()
  const { runAction } = useNotificationActions()
  const user = computed(() => getters.user)

  let unsubscribeFromNotifications
  let unsubscribeFromReconnect
  let stopWatchingUser
  let disposed = false

  const unsubscribe = () => {
    unsubscribeFromNotifications?.()
    unsubscribeFromNotifications = undefined
    unsubscribeFromReconnect?.()
    unsubscribeFromReconnect = undefined
  }

  const toastNotification = (notification) => {
    if (notification?.readAt || !notification?.uiBehaviours?.includes(UI_BEHAVIOUR.TOAST)) {
      return
    }

    queueNotificationToast(
      {
        notificationType: notification.notificationType,
        status: notification.status,
        runArgs: notification.runArgs,
        uiBehaviours: notification.uiBehaviours,
        unread: !notification.readAt,
        time: 'just now',
        onClick: () => runAction(notification)
      },
      { timeout: NOTIFICATION_TIME_ON_SCREEN }
    )
  }

  const subscribe = async (signedInUser) => {
    try {
      const token = await signedInUser.getIdToken()
      await socketService.connect(token)

      // This is for edge case of a logout / login with diff account
      if (user.value !== signedInUser) {
        socketService.disconnect()
        return
      }

      unsubscribe()
      unsubscribeFromNotifications = socketService.on(
        NOTIFICATIONS_UPDATED_EVENT,
        (notification) => {
          queryClient.invalidateQueries({ queryKey: NOTIFICATION_ALL_INVALIDATION_KEY })
          queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_INVALIDATION_KEY })
          toastNotification(notification)
        }
      )

      unsubscribeFromReconnect = socketService.on('connect', () => {
        logger.info('[use-notifications] Socket reconnected, fetching notifications')
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_ALL_INVALIDATION_KEY })
        queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_INVALIDATION_KEY })
      })
    } catch (error) {
      logger.error('[use-notifications] Failed to connect notifications socket:', error.message)
    }
  }

  const startSocket = () => {
    if (disposed || stopWatchingUser) return

    stopWatchingUser = watch(
      user,
      (signedInUser) => {
        if (signedInUser) {
          subscribe(signedInUser)
          return
        }

        unsubscribe()
        socketService.disconnect()
      },
      { immediate: true }
    )
  }

  if (autoConnect) startSocket()

  onUnmounted(() => {
    disposed = true
    stopWatchingUser?.()
    unsubscribe()
    socketService.disconnect()
  })

  return startSocket
}
